import { ArrowCircleDownIcon } from '@heroicons/react/solid';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import ProductCartIcon from '../../../../../assets/Icons/ProductCartIcon';
import { localCart } from '../../../../atoms/LocalCart';
import { localUserID, user } from '../../../../atoms/user';
import {
  addToCart,
  addToRecentlyViewed,
  getCartCount,
} from '../../../../services/Cart';
import { AddToCartProps } from '../../../../utils/interfaces/cart';
import { BundleData } from '../../../../utils/interfaces/Products/bundles';
import { User } from '../../../../utils/interfaces/User';
import ButtonDefault, {
  ButtonVariants,
  Spinner,
} from '../../../shared/basic/button';
import { ShouldRender } from '../../../shared/basic/ShouldRender';
import { formateErrorObject } from '../../../shared/formateErrorObject';
import { notify } from '../../../shared/notify';

interface Props {
  data: BundleData;
}

export type Item = {
  category: string;
};

const BundleCard: React.FC<Props> = ({ data }) => {
  const localUserId = useRecoilValue(localUserID);
  const [loading, setLoading] = useState({ value: false, id: 0 });
  const router = useRouter();
  const [, setLocalCartCount] = useRecoilState(localCart);
  const userData: User | undefined = useRecoilValue(user);
  const screens = useBreakpoint();

  const handleCartCount = async () => {
    try {
      const data = await getCartCount({});
      setLocalCartCount(data.count);
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    }
  };

  const handleAddDataToCart = async (payload: AddToCartProps) => {
    try {
      setLoading({ value: true, id: payload.id });

      await addToCart(payload);
      addToRecentlyViewed(payload.id, userData?.zl_uid);
      router.push('/cart');
      handleCartCount();
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setLoading({ value: false, id: payload.id });
    }
  };

  const countBooksAndCourses = (
    items: Item[],
  ): { Books: number; Courses: number } =>
    items?.reduce(
      (acc, item) => {
        if (item.category === 'Book' || item.category === 'E-Book') {
          acc.Books++;
        } else if (
          item.category === 'Recorded Course' ||
          item.category === 'Course'
        ) {
          acc.Courses++;
        }
        return acc;
      },
      { Books: 0, Courses: 0 },
    );

  return (
    <div className='w-full h-auto border-[#1C1C1C] border  bg-gradient-to-b from-[#151515] to-black hover:bg-cartBgColor card rounded-[12px] cursor-pointer flex flex-col transition duration-300'>
      <div className='h-full  card rounded-md text-fadedWhite'>
        <div className='flex flex-col items-center h-full w-full '>
          <div className='w-full max-h-[170px] py-3 md:pt-6 md:pb-0 flex justify-center h-full xl:p-5'>
            <Image
              src={data?.image}
              alt={data?.heading}
              width={392}
              height={170}
              className='object-contain rounded-md w-full'
            />
          </div>
          <div className='flex items-end md:flex-col p-4 lg:p-6 w-full'>
            <div className='w-full flex flex-col justify-start gap-y-1 items-start'>
              <span className='text-textLightGray text-[10px] md:text-sm'>
                {countBooksAndCourses(data?.products)?.Books} Books +{' '}
                {countBooksAndCourses(data?.products)?.Courses} Courses
              </span>
              <span className='text-sm md:text-lg font-semibold'>
                {data?.heading}
              </span>
              <ShouldRender check={data?.is_available}>
                <span className='flex gap-x-2 items-baseline pl-1'>
                  <p className='line-through text-[#77777C] text-sm md:text-base font-semibold whitespace-nowrap'>
                    {data?.currency} {data?.actualPriceHardCopy}
                  </p>
                  <p className='text-sm md:text-base font-bold whitespace-nowrap'>
                    {data?.currency} {data?.hardCopyPrice}
                  </p>
                  <ShouldRender check={screens.xl || !screens.lg}>
                    {data?.actualPriceHardCopy && data?.hardCopyPrice ? (
                      <p className='flex gap-x-1 text-[#16A34A] font-bold text-[10px] md:text-sm items-center'>
                        {Math.round(
                          ((data.actualPriceHardCopy -
                            Number(data.hardCopyPrice)) /
                            data.actualPriceHardCopy) *
                          100,
                        )}
                        %{' '}
                        <ArrowCircleDownIcon className='w-3 h-3 md:w-5 md:h-5' />
                      </p>
                    ) : null}
                  </ShouldRender>
                </span>
              </ShouldRender>
            </div>
            <ShouldRender check={!screens.md}>
              <ButtonDefault
                size={0}
                isWidthFull={true}
                rounded={false}
                roundedSm={true}
                variant={ButtonVariants.WHITE}
                available={data?.is_available}
                enableScaling={false}
                loading={data.hardCopyId === loading.id && loading.value}
                disabled={data.hardCopyId === loading.id && loading.value}
                onClick={(e) => {
                  e.stopPropagation();

                  handleAddDataToCart({
                    id: data.hardCopyId as number,
                    temp_user_id: localUserId.toString(),
                    quantity: 1,
                    trackingData: {
                      productData: {
                        price: data.hardCopyPrice as number,
                        id: Number(data.hardCopyId),
                        heading: data.heading as string,
                        quantity: 1 as number,
                        currency: data?.i18n_attributes?.currency,
                      },
                      userData,
                      pathname: router.pathname,
                      productType: 'hardcopy',
                      type: 'bundles',
                    },
                  });
                }}
                className=' rounded-lg bg-white flex justify-center items-center'
              >
                {' '}
                <div className='pl-2 pr-2.5 py-[18px]'>
                  <ShouldRender check={data.hardCopyId !== loading.id}>
                    <ProductCartIcon />
                  </ShouldRender>
                  <ShouldRender check={data.hardCopyId === loading.id}>
                    <Spinner />
                  </ShouldRender>
                </div>
              </ButtonDefault>
            </ShouldRender>
            <ShouldRender check={screens.md}>
              <div className='w-full md:mt-4'>
                <ButtonDefault
                  size={1}
                  isWidthFull={true}
                  rounded={false}
                  available={data?.is_available}
                  roundedSm={true}
                  variant={ButtonVariants.WHITE}
                  enableScaling={false}
                  loading={data.hardCopyId === loading.id && loading.value}
                  disabled={data.hardCopyId === loading.id && loading.value}
                  onClick={(e) => {
                    e.stopPropagation();

                    handleAddDataToCart({
                      id: data.hardCopyId as number,
                      temp_user_id: localUserId.toString(),
                      quantity: 1,
                      trackingData: {
                        productData: {
                          price: data.hardCopyPrice as number,
                          id: Number(data.hardCopyId),
                          heading: data.heading as string,
                          quantity: 1 as number,
                          currency: data?.i18n_attributes?.currency,
                        },
                        userData,
                        pathname: router.pathname,
                        productType: 'hardcopy',
                        type: 'bundles',
                      },
                    });
                  }}
                  className='w-full text-center text-sm group whitespace-nowrap'
                >
                  <span className='text-sm font-semibold  px-3 xl:px-5'>
                    Add to Cart
                  </span>
                </ButtonDefault>
              </div>
            </ShouldRender>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BundleCard;
